import { useState } from "react";
import { Link } from "react-router-dom";
import type { TipoDeOrfao, TituloOrfao } from "@/api/contrato";
import { TETO_DA_PAGINA, useTitulosOrfaos, useTratarTituloOrfao } from "@/api/consultas";
import { Botao } from "@/componentes/Botao";
import { useAvisos } from "@/componentes/Avisos";
import { Confirmacao, type PedidoDeConfirmacao } from "@/componentes/Confirmacao";
import { Dica } from "@/componentes/Dica";
import { FaixaDeErro } from "@/componentes/FaixaDeErro";
import { Pilula } from "@/componentes/PilulaDeSituacao";
import { useFiltrosNaUrl, type DefinicaoDeFiltro } from "@/componentes/useFiltrosNaUrl";
import { dataHora, dia, dinheiro, faz, idCurto } from "@/formato";
import { rotaDoBoleto, rotaDoConvenioPorId } from "@/rotas";
import { useSessao } from "@/sessao/useSessao";
import { contagemDaLista } from "@/telas/boletos/Boletos";
import { fraseDaRecusa } from "@/telas/boletos/recusas";

/*
 * Títulos órfãos: o gate para o caso de todos os outros falharem.
 *
 * ┌──────────────────────────────────────────────────────────────────────────┐
 * │ REGISTRA E NÃO CORRIGE (doc 05 §7).                                      │
 * │                                                                          │
 * │ Um título no provider sem boleto nosso é a cara de uma duplicidade       │
 * │ consumada, e autocorreção silenciosa esconderia o defeito. "Marcar como  │
 * │ tratado" é uma pessoa dizendo que olhou: nenhum boleto muda, e o título  │
 * │ continua no provider. Por isso o gesto é `critica`: tirar um órfão da    │
 * │ visão geral é apagar o único alarme daquele título.                      │
 * └──────────────────────────────────────────────────────────────────────────┘
 *
 * Em cartões, como Divergências: a linha de `Tabela` abre alguma coisa, e o órfão `sem_boleto`
 * não tem o que abrir.
 *
 * A linha não tem dado do sacado, por construção: a tabela só guarda identificadores, valor e
 * vencimento, e por isso não há o que mascarar pelo papel.
 *
 * `satisfies` reprova aqui o tipo novo do contrato, em vez de deixá-lo cru na tela.
 */
type ChaveDoFiltro = "incluir_tratados";

const DEFINICOES: readonly DefinicaoDeFiltro<ChaveDoFiltro>[] = [
  { chave: "incluir_tratados", rotulo: "Incluir tratados" },
];

const TIPOS = {
  sem_boleto: {
    rotulo: "Sem boleto",
    dica: "Nenhum boleto nosso corresponde a este título.",
  },
  segundo_titulo_do_boleto: {
    rotulo: "Segundo título",
    dica: "A referência aponta um boleto nosso, e o título é outro.",
  },
  numero_de_outro_titulo: {
    rotulo: "Número de outro título",
    dica: "O nosso número é de um boleto com outro título no provider.",
  },
} satisfies Record<TipoDeOrfao, { rotulo: string; dica: string }>;

function pedidoDeTratamento(orfao: TituloOrfao): PedidoDeConfirmacao {
  return {
    severidade: "critica",
    titulo: `Marcar o título ${orfao.nosso_numero ?? idCurto(orfao.provider_id)} como tratado?`,
    corpo: "Sai do alerta. O título continua no provider, e nenhum boleto muda.",
    verbo: "Marcar como tratado",
    exigeJustificativa: true,
    identidade: orfao.id,
  };
}

export function TitulosOrfaos() {
  const { cliente, estado, pode } = useSessao();
  const avisos = useAvisos();
  const filtros = useFiltrosNaUrl(DEFINICOES);

  const [alvo, setAlvo] = useState<TituloOrfao | null>(null);

  const incluirTratados = filtros.valores.incluir_tratados === "true";
  const { data, error, isPending } = useTitulosOrfaos(
    cliente,
    incluirTratados,
    estado.fase === "dentro",
  );
  const tratar = useTratarTituloOrfao(cliente);
  const podeOperar = pode("operador");

  const itens = data?.titulos_orfaos ?? [];

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-3">
        {itens.length > 0 && error === null && (
          <span className="flex items-center gap-1">
            <span className="rotulo">
              {contagemDaLista(
                { itens, temMais: itens.length >= TETO_DA_PAGINA },
                "título",
                "títulos",
              )}
            </span>
            <Dica texto="A rodada noturna compara os títulos do provider com os nossos boletos, registra e não corrige." />
          </span>
        )}
        <label className="ml-auto flex items-center gap-2 text-corpo text-texto-2">
          <input
            type="checkbox"
            checked={incluirTratados}
            onChange={(evento) =>
              filtros.ajustar("incluir_tratados", evento.target.checked ? "true" : "")
            }
          />
          Incluir tratados
        </label>
      </div>

      {error !== null ? (
        <FaixaDeErro erro={error} />
      ) : isPending ? (
        <div aria-hidden="true" className="h-32 rounded-cartao bg-superficie" />
      ) : itens.length === 0 ? (
        <p className="text-texto-2">Nenhum título órfão.</p>
      ) : (
        itens.map((orfao) => (
          <CartaoDoOrfao
            key={orfao.id}
            orfao={orfao}
            aoTratar={podeOperar ? () => setAlvo(orfao) : undefined}
          />
        ))
      )}

      <Confirmacao
        pedido={alvo === null ? null : pedidoDeTratamento(alvo)}
        aoCancelar={() => setAlvo(null)}
        aoConfirmar={async (justificativa) => {
          if (alvo === null) return;

          try {
            await tratar.mutateAsync({ id: alvo.id, justificativa });
          } catch (falha) {
            // Fica no diálogo, com a frase nossa: a justificativa escrita não se perde.
            throw new Error(fraseDaRecusa(falha), { cause: falha });
          }
          setAlvo(null);
          avisos.feito("Título marcado como tratado");
        }}
      />
    </div>
  );
}

const LINK = "text-esferografica underline-offset-2 hover:underline";

function CartaoDoOrfao({
  orfao,
  aoTratar,
}: {
  orfao: TituloOrfao;
  /** Sem ela, quem só lê não vê a ação. */
  aoTratar: (() => void) | undefined;
}) {
  const tipo = TIPOS[orfao.tipo];
  const tratado = orfao.tratado_em !== undefined;

  return (
    <article className="rounded-cartao border border-fio bg-superficie p-3">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="flex items-center gap-1">
              <Pilula matiz="falha">{tipo.rotulo}</Pilula>
              <Dica texto={tipo.dica} />
            </span>
            <span className="dados truncate text-corpo text-texto" title={orfao.provider_id}>
              {orfao.provider_id}
            </span>
            {orfao.situacao_provider !== undefined && (
              <span className="dados rounded-full border border-fio-forte bg-superficie-2 px-2 py-[2px] text-rotulo uppercase tracking-[0.08em] text-texto-2">
                {orfao.situacao_provider}
              </span>
            )}
          </div>

          <div className="mt-2 flex flex-wrap gap-3 text-[12px] text-texto-2">
            {orfao.nosso_numero !== undefined && (
              <span>
                nosso número <span className="dados">{orfao.nosso_numero}</span>
              </span>
            )}
            {orfao.valor_centavos !== undefined && (
              <span className="tabular">{dinheiro(orfao.valor_centavos)}</span>
            )}
            {orfao.vencimento !== undefined && (
              <span>
                vence <span className="dados">{dia(orfao.vencimento)}</span>
              </span>
            )}
            {orfao.boleto_id !== undefined && (
              <Link to={rotaDoBoleto(orfao.boleto_id)} title={orfao.boleto_id} className={LINK}>
                boleto <span className="dados">{idCurto(orfao.boleto_id)}</span>
              </Link>
            )}
            <Link
              to={rotaDoConvenioPorId(orfao.convenio_id)}
              title={orfao.convenio_id}
              className={LINK}
            >
              convênio <span className="dados">{idCurto(orfao.convenio_id)}</span>
            </Link>
            <span>
              {/* Vezes separa título de passagem de defeito que persiste, como na divergência. */}
              visto <span className="dados">{orfao.vezes}</span>×
            </span>
            <span title={dataHora(orfao.visto_em)}>
              última há <span className="dados">{faz(orfao.visto_em)}</span>
            </span>
          </div>

          {tratado && (
            <p className="mt-2 flex items-center gap-1 text-[12px] text-texto-2">
              <Pilula matiz="encerrado">Tratado</Pilula>
              <span>
                {orfao.tratado_por_nome ?? "Operador"}, {dataHora(orfao.tratado_em)}
              </span>
              {orfao.tratado_motivo !== undefined && <Dica texto={orfao.tratado_motivo} />}
            </p>
          )}
        </div>

        {aoTratar !== undefined && !tratado && (
          <Botao tom="secundario" onClick={aoTratar}>
            Marcar como tratado
          </Botao>
        )}
      </div>
    </article>
  );
}
